<script setup lang="ts">
import {ref} from "vue";
import SearchInput from "@/core/components/search-bar/SearchInput.vue";
import {drugInteractionApi} from "@/container";
import Icon from "@/core/components/icon/Icon.vue";
import {Badge} from "@/core/components/ui/badge";
import {Button} from "@/core/components/ui/button";
import {VidalAllergy} from "@/customer/interfaces/Patient";

let allergies = defineModel<Omit<VidalAllergy, 'id'>[]>('allergies',{required: true});

const allergiesLoading = ref(false);
const allergiesSearchQuery = ref('');
const searchResult = ref<Omit<VidalAllergy, 'id'>[]>();
let timer: NodeJS.Timeout;

/**
 * Search vidal allergies.
 * @param query
 */
const search = (query: string) => {
    allergiesSearchQuery.value = query;
    if (timer) {
        clearTimeout(timer);
    }

    if (query.length === 0 || query.length < 3) {
        searchResult.value = undefined;
    } else {
        timer = setTimeout(async () => {
            allergiesLoading.value = true;
            await drugInteractionApi().allergies(query)
                .then(response => {
                    searchResult.value = response;
                })
                .finally(() => allergiesLoading.value = false);
        }, 500);
    }
};

/**
 * Update selected allergies.
 * @param allergy
 */
const updateAllergies = (allergy: Omit<VidalAllergy, 'id'>) => {
    const allergyExists = allergies.value.find(selected => selected.vidal_id === allergy.vidal_id);

    if (!allergyExists) {
        allergies.value.push(allergy);
    } else {
        const indexToRemove = allergies.value.indexOf(allergyExists);
        if (indexToRemove !== -1) {
            allergies.value.splice(indexToRemove, 1);
        }
    }

    allergiesSearchQuery.value = '';
    searchResult.value = undefined;
};
</script>

<template>
  <div class="relative">
    <search-input
      class="w-full form-input"
      :is-loading="allergiesLoading"
      :model-value="allergiesSearchQuery"
      placeholder="Rechercher des allergies..."
      @input="search"
    />
    <div
      v-if="allergiesLoading || searchResult"
      class="absolute top-full left-0 right-0 z-50 mt-1 shadow-lg border bg-white"
    >
      <div class="p-2">
        <div v-if="allergiesLoading" class="flex items-center justify-center py-4">
          <div class="flex items-center space-x-2 text-muted-foreground">
            <span class="text-sm">Recherche en cours...</span>
          </div>
        </div>

        <div v-else-if="searchResult" class="max-h-40 overflow-x-clip overflow-y-scroll">
          <div v-if="searchResult.length > 0" class="space-y-1">
            <Button
              v-for="allergy in searchResult"
              :key="allergy.vidal_id"
              variant="ghost"
              class="w-full justify-start h-auto p-3 text-left"
              @click="updateAllergies(allergy)"
            >
              {{ allergy.title }}
            </Button>
          </div>

          <div v-else class="py-4 text-center">
            <p class="text-sm text-muted-foreground">
              Aucun résultat trouvé
            </p>
          </div>
        </div>
      </div>
    </div>
    <div v-if="allergies.length > 0">
      <div class="h-px bg-gray-300 my-2" />
      <div class="space-y-3">
        <div class="flex flex-wrap gap-2">
          <Badge
            v-for="allergy in allergies"
            :key="allergy.vidal_id"
            variant="secondary"
            class="px-3 py-1 cursor-pointer flex items-center"
            @click="updateAllergies(allergy)"
          >
            <span>{{ allergy.title }}</span>
            <Icon
              name="mdi-close"
              :size="15"
              class="ml-1 h-2 w-2 group-hover:text-destructive-foreground"
            />
          </Badge>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
:deep(.form-input) {
    @apply focus:ring-primary
}
</style>
